import React from 'react';
import { Check, AlertCircle } from 'lucide-react';

interface SubmitButtonProps {
  selectedAnswer: string;
  isSubmitting: boolean;
  onSubmit: () => void;
  submitError?: string | null;
  confidence: number | null;
  /** The signed-out diagnostic has no confidence scale, so it gates on the answer alone. */
  requireConfidence?: boolean;
}

/**
 * The .pen submit affordance (node 3mLdc / o1zel): a teal ledge button with the
 * "trust your instinct" subcopy. Submit stays gated on an answer AND a confidence
 * rating — the same contract as before, so the adaptive metadata always rides
 * along with the answer.
 */
export const SubmitButton: React.FC<SubmitButtonProps> = ({
  selectedAnswer,
  isSubmitting,
  onSubmit,
  submitError,
  confidence,
  requireConfidence = true,
}) => {
  const confidenceMissing = requireConfidence && confidence === null;
  const canSubmit = !!selectedAnswer && !confidenceMissing && !isSubmitting;

  const label = isSubmitting
    ? 'Submitting…'
    : !selectedAnswer
      ? 'Select an answer to continue'
      : confidenceMissing
        ? 'Rate your confidence to continue'
        : 'Submit answer';

  return (
    <div className="flex flex-col items-center gap-2 pt-1">
      <button
        onClick={onSubmit}
        disabled={!canSubmit}
        className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-primary-500 px-10 py-4 font-sans text-base font-extrabold text-white shadow-ledge-brand transition-all hover:bg-primary-600 active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed ${
          isSubmitting ? 'cursor-wait' : ''
        }`}
      >
        {isSubmitting ? (
          <span className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
        ) : (
          canSubmit && <Check className="h-5 w-5" />
        )}
        {label}
      </button>

      {canSubmit && !submitError && (
        <p className="text-[13px] font-semibold text-neutral-600">
          Trust your instinct. There's no going back once you submit.
        </p>
      )}

      {submitError && (
        <div className="mt-2 w-full rounded-r-xl border-l-4 border-error bg-error-light p-4" role="alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-error" />
            <div>
              <p className="font-medium text-neutral-900">Unable to submit answer</p>
              <p className="text-sm text-neutral-700">{submitError}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
